// Qué es un filtro colgado de la cabecera de una columna, y las dos cuentas
// que se le hacen.
//
// Está separado de `CabeceraFiltrable` —que es quien lo pinta— porque esto no
// necesita React para nada y así se puede probar sin montar un árbol: el
// recuento de condiciones activas decide el resalte de la cabecera, el
// contador y el número de «filtros activos» de la pantalla, y equivocarse en él
// no da error, sólo un número mal.
import type { OpcionMulti } from '@/components/common/MultiFiltro'

export interface SeccionLista {
  tipo: 'lista'
  /** Nombre de la pestaña; sólo se pinta si la columna ofrece más de una. */
  nombre?: string
  opciones: OpcionMulti[]
  seleccion: string[]
  onSeleccion: (valores: string[]) => void
  /** Un valor a la vez: elegir otro sustituye al anterior. */
  unico?: boolean
  /** Títulos en monoespaciada. Cierto para códigos, falso para nombres. */
  titulosMono?: boolean
  /** Enter —y pegar una columna de Excel— añaden lo escrito aunque no esté en
   *  el catálogo. Es lo que permite pegar una lista de nº de carga. */
  permitirLibre?: boolean
}

export interface SeccionRango {
  tipo: 'rango'
  nombre?: string
  desde: string
  hasta: string
  onDesde: (v: string) => void
  onHasta: (v: string) => void
}

export type SeccionFiltro = SeccionLista | SeccionRango

/** Lo que una columna deja filtrar. Más de una sección = pestañas. */
export type FiltroColumna = SeccionFiltro[]

/**
 * Cuántas condiciones tiene puestas esta columna.
 *
 * Un rango cuenta UNA aunque traiga las dos fechas: «del 1 al 5» es una
 * condición dicha con dos números, y contarla doble inflaría el recuento de
 * filtros activos de la pantalla sin que nada lo delate.
 */
export function activosDe(filtro: FiltroColumna): number {
  return filtro.reduce((n, s) => n + (
    s.tipo === 'rango' ? (s.desde || s.hasta ? 1 : 0) : s.seleccion.length
  ), 0)
}

/** Quita todo lo de esta columna, sección a sección.
 *
 *  Las llamadas van en cadena y cada una actualiza por función, así que las dos
 *  mitades de un rango no se pisan: con `setEstado({...estado, desde: ''})` la
 *  segunda llamada habría devuelto la primera a su sitio. */
export function limpiarFiltro(filtro: FiltroColumna): void {
  for (const s of filtro) {
    if (s.tipo === 'rango') { s.onDesde(''); s.onHasta('') } else s.onSeleccion([])
  }
}

/**
 * ¿Esta sección tiene algo que ofrecer?
 *
 * Una lista sin opciones abriría un panel vacío, que es peor que no tener
 * embudo: parece un filtro roto. Se conserva, eso sí, la que no tiene catálogo
 * pero SÍ selección —puede venir de un enlace guardado— o no habría forma de
 * quitarla desde la columna.
 */
export function tieneAlgoQueOfrecer(s: SeccionFiltro): boolean {
  return s.tipo === 'rango' || s.opciones.length > 0 || s.seleccion.length > 0
}
